import React, { useContext, useRef, useState } from 'react';

import { useTwcg2304ImportInOnboarding } from '@confluence/experiment-twcg-2304-import-in-onboarding/entry-points/useTwcg2304ImportInOnboarding.ts';
import { FormattedMessage } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { ImportModalTypes } from '../Modals/ImportModalTypes.ts';
import { ProgressStatus } from '../Modals/ProgressModal/progress-types.ts';
import { useValidateBulkDocuments } from '../../hooks/useValidateBulkDocuments.ts';
import { useHandleRejectedDocuments } from '../../hooks/useHandleRejectedDocuments.ts';
import { useHandleBulkProcessingErrors } from '../../hooks/useHandleBulkProcessingErrors.ts';
import { useAddAbortControllerDocuments } from '../../hooks/useAddAbortControllerDocuments.ts';
import { DocErrors } from '../../hooks/useUploadReducer.ts';
import { createParentPage, deletePage } from '../../services/DocImportService.ts';
import { checkIsAllDocsNotLoaded } from '../../utils/checkIsAllDocsNotLoaded.ts';
import { REQUEST_ABORTED } from '../../utils/cfetchWithAbort.ts';
import { i18n } from '../Modals/CommonErrorModal.tsx';
import { i18n as bulkLimitModalText } from '../Modals/BulkLimitModal.tsx';
import { useHandleDeleteParentPage } from '../../hooks/useHandleDeleteParentPage.ts';
import { ImportStateContext } from '../ImportStateContextProvider.tsx';
import { parentPageTypes } from '../../utils/getRestrictionType.ts';
import { useShowRestrictionWarningModal } from '../../hooks/useShowRestrictionWarningModal.ts';
import { useBulkLimitCheck } from '../../hooks/useBulkLimitCheck.ts';
import type { ImportStrategyType, UploadStrategyType } from '../types.ts';
import { handleBulkExport } from '../../utils/handleExportedArray.ts';
export const useUploadBulk = (
	setIsCancelledDocumentsDeleted: any,
	currentSpace: any,
	restrictionType: any,
	parentPageId: any,
	handleUploadFileWithDiscard: any,
	setImportedPageData: any,
	importStrategy: React.MutableRefObject<React.MutableRefObject<ImportStrategyType> | null>,
	subType: any,
	deleteOnboardingBlankPage?: () => Promise<void>,
): UploadStrategyType => {
	const {
		showModal,
		closeModal,
		updateModalData,
		docs,
		docActions: { uploadDocSuccess },
	} = useContext(ImportStateContext);
	const [bulkError, setBulkError] = useState<string | null>(null);
	const validateBulkDocuments = useValidateBulkDocuments();
	const handleRejectedDocuments = useHandleRejectedDocuments(setBulkError);
	const handleBulkProcessingErrors = useHandleBulkProcessingErrors();
	const addAbortControllerDocuments = useAddAbortControllerDocuments();
	const [bulkParentPageId, setBulkParentPageId] = useState<string | null>(null);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const showRestrictionWarningModal = useShowRestrictionWarningModal();
	const bulkLimitCheck = useBulkLimitCheck();
	const { isEnabled: isUploadInOnboardingEnabled, loading: isUploadInOnboardingLoading } =
		useTwcg2304ImportInOnboarding();
	const isExperimentActive = !isUploadInOnboardingLoading && isUploadInOnboardingEnabled;
	useHandleDeleteParentPage(bulkError, bulkParentPageId, setBulkParentPageId);

	//TODO: WTF?
	const docsRef = useRef(docs);
	docsRef.current = docs;

	const handleDeleteCancelledDocuments = () => {
		setIsCancelledDocumentsDeleted(false);
		const cancelledDocuments = docsRef.current.filter(
			(doc) => doc.error?.type === DocErrors.REQUEST_ABORTED_ERROR && doc.id,
		);

		try {
			cancelledDocuments.forEach(async (cancelledDoc) => {
				await deletePage(cancelledDoc.id, { isPublicPage: true });
			});
		} catch (e) {
			return;
		}
		setIsCancelledDocumentsDeleted(true);
		checkIsAllDocsNotLoaded(docsRef.current, setBulkError);
	};

	const handleCreateParentPage = async () => {
		try {
			const folderName = isExperimentActive
				? importStrategy.current?.current?.folderName
				: undefined;
			const effectiveRestrictionType = isExperimentActive ? 'free' : restrictionType;
			const createParentPageResult = folderName
				? await createParentPage(
						currentSpace,
						parentPageId,
						effectiveRestrictionType,
						subType,
						folderName,
						true,
					)
				: await createParentPage(currentSpace, parentPageId, effectiveRestrictionType, subType);
			const {
				data: { id },
			} = createParentPageResult;
			setBulkParentPageId(id);
			return id;
		} catch (err) {
			if (err === REQUEST_ABORTED) return;
			closeModal(ImportModalTypes.PROGRESS);

			//TODO: refactor: create a separate modal
			showModal(ImportModalTypes.UPLOAD_ERROR, {
				headText: <FormattedMessage {...i18n.failedBulkImportPopupTitle} />,
				bodyText: <FormattedMessage {...i18n.failedBulkImportPopupContent} />,
				submitText: <FormattedMessage {...bulkLimitModalText.bulkLimitModalDialogRetryAction} />,
				modalWidth: 'small',
			});
		}
	};

	const sendBulkAnalytics = (actionSubjectId, attributes = {}) => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'IFSBulkUpload',
				action: 'clicked',
				actionSubject: 'bulkUploads',
				actionSubjectId,
				attributes: {
					...attributes,
				},
			},
		}).fire();
	};

	const validateDocuments = (documents: (File | null)[]): File[] | [] | false => {
		const validatedDocuments = validateBulkDocuments(documents);
		if (!validatedDocuments.length) {
			updateModalData(ImportModalTypes.PROGRESS, {
				status: ProgressStatus.ERROR,
			});
			return false;
		}

		return validatedDocuments;
	};

	const handleUpload = async (documents: (File | null)[]) => {
		const importStrategyRef = importStrategy.current;
		//TODO: move it
		setBulkError(null);

		const validatedDocuments = validateDocuments(documents);
		if (!validatedDocuments) return;
		const validatedDocumentsWithAbortSignal = addAbortControllerDocuments(validatedDocuments);

		try {
			sendBulkAnalytics(`${importStrategyRef!.current.key}BulkUpload`, {
				documentsCount: documents.length,
			});
			const createdBulkParentPageId = await handleCreateParentPage();
			if (!createdBulkParentPageId) return false;
			setImportedPageData({ id: createdBulkParentPageId, space: currentSpace });
			//TODO: move into a function
			const response = await Promise.allSettled(
				validatedDocumentsWithAbortSignal
					.map(async ({ doc, signal }: { doc: File; signal: AbortSignal }) => {
						const {
							data: { title, id },
							errors,
						} = await handleUploadFileWithDiscard(doc, {
							createdBulkParentPageId,
							isBulkUpload: true,
							signal,
						});
						if (errors) {
							handleBulkProcessingErrors(title, errors);
						}
						uploadDocSuccess(title, id);
						return id;
					})
					.filter((id: Promise<string | undefined>) => id),
			);

			await handleRejectedDocuments(response, validatedDocuments);
			void handleDeleteCancelledDocuments();

			// When the experiment created a separate parent page for the bulk import, the blank page
			// originally created (e.g. via handleImportCTAClick) is not replaced by the normal discard
			// mechanism. Delete it explicitly.
			if (isExperimentActive && createdBulkParentPageId && deleteOnboardingBlankPage) {
				void deleteOnboardingBlankPage();
			}

			// When the experiment is active, navigate to the first successfully uploaded child page
			// instead of the parent folder so the user lands on actual imported content.
			if (isExperimentActive) {
				const firstFulfilledId = response.find(
					(r): r is PromiseFulfilledResult<string> => r.status === 'fulfilled' && !!r.value,
				)?.value;
				if (firstFulfilledId) {
					setImportedPageData({ id: firstFulfilledId, space: currentSpace });
				}
			}

			updateModalData(ImportModalTypes.PROGRESS, {
				status: ProgressStatus.SUCCESS,
			});
		} catch (error) {
			updateModalData(ImportModalTypes.PROGRESS, {
				status: ProgressStatus.ERROR,
			});
		}
	};

	const restrictionCheck = async (): Promise<boolean> => {
		if (restrictionType !== parentPageTypes.RESTRICTED && !isExperimentActive) {
			return showRestrictionWarningModal();
		}

		return true;
	};

	const validateBeforeProgress = async (documents) => {
		// don't use destructuring on the top of the file to keep the link to the original strategy
		const importStrategyRef = importStrategy.current;
		if (!(await restrictionCheck())) return false;

		if (importStrategyRef!.current.validateBulkUploadBeforeProgress) {
			return importStrategyRef!.current.validateBulkUploadBeforeProgress(documents);
		}

		return bulkLimitCheck(documents);
	};

	const handleFilesBeforeUpload = async (documents) => {
		// don't use destructuring on the top of the file to keep the link to the original setrategy
		const importStrategyRef = importStrategy.current;

		if (importStrategyRef!.current.exportFile) {
			return handleBulkExport(documents, importStrategyRef!.current.exportFile);
		}

		return documents;
	};

	const validateBeforeUpload = async (documents) => {
		// don't use destructuring on the top of the file to keep the link to the original strategy
		const importStrategyRef = importStrategy.current;

		if (importStrategyRef!.current.validateBulkBeforeUpload) {
			return importStrategyRef!.current.validateBulkBeforeUpload(documents);
		}

		return validateDocuments(documents);
	};

	return {
		handleUpload,
		validateBeforeProgress,
		handleFilesBeforeUpload,
		validateBeforeUpload,
	};
};
